import {Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {UserCardContentRequest} from "../interface/userCardContentRequest";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ContentService} from "../service/content.service";
import {ContentType} from "../interface/contentType";
import {UserCardUtil} from "../utilityClass/userCardUtil";

@Component({
  selector: 'app-edit-content-dialog',
  templateUrl: './edit-content-dialog.component.html',
  styleUrls: ['./edit-content-dialog.component.scss', '../add-user-card-content/add-user-card-content.component.scss']
})
export class EditContentDialogComponent implements OnInit {

  protected editForm: FormGroup;
  protected contentIcon: string;

  protected selectedFile: File | any;
  protected selectedFileName: string | any;
  protected showError: boolean = false;
  protected fileSizeExceeded: boolean = false;
  protected hasDocumentURL: boolean = false;

  @ViewChild('documentInput') documentInput: ElementRef<HTMLInputElement> | null | undefined;

  constructor(
    @Inject(MAT_DIALOG_DATA) private dataFromConfig: UserCardContentRequest,
    private dialogRef: MatDialogRef<EditContentDialogComponent>,
    private fb: FormBuilder,
    private contentService: ContentService,
    private userCardUtil: UserCardUtil
  ) {
    this.editForm = this.fb.group({
      placeholder: ['', [Validators.required, this.userCardUtil.whitespaceValidator()]],
      linkTitle: [this.data.linkTitle, [this.userCardUtil.whitespaceValidator(), Validators.maxLength(10)]],
    }, {updateOn: 'blur'});

    this.contentIcon = this.contentService.getImage(this.data.contentIcon);
  }

  ngOnInit(): void {
    this.initForm();

    if(this.isDocument()) {
      this.initHasDocumentURL();
    }
  }

  get data() {
    return this.dataFromConfig;
  }

  get editUserCardContentControl() {
    return this.editForm.controls;
  }

  get documentName() {
    const filename = this.selectedFileName;
    return filename ? filename : 'No file chosen';
  }

  protected isDocument(): boolean {
    return this.data.type === ContentType.DOCUMENT
  }


  protected isContact(): boolean {
    return this.data.type === ContentType.CONTACT;
  }

  protected edit() {
    this.editForm.markAllAsTouched()

    if(this.isDocument() && this.isBothDocumentFieldsInvalid()) {
      this.showError = true
    }

    if(!this.getPlaceholder() || this.editForm.invalid || this.showError || this.fileSizeExceeded) {
      return;
    }

    this.data.linkTitle = this.editForm.get('linkTitle')?.value;
    this.data.placeholder = this.getPlaceholder();
    this.closeDialog({updatedContent: this.data, document: this.selectedFile, isUpdated: true});
  }

  protected closeDialog(data?: any): void {
    this.dialogRef.close(data);
  }

  protected initHasDocumentURL(): void {
    this.editForm.get('placeholder')?.valueChanges.subscribe({
      next: () => {
        this.showError = false;
        const value = this.editForm.get('placeholder')?.value;
        this.hasDocumentURL = (value && value.length > 0);
      }
    })
  }

  protected clickChooseFile(inputField: any): void {
    if (this.hasDocumentURL) return;
    inputField.click();
  }

  protected onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const fileSizeInMB = file.size / (1024 * 1024);
      const maxSizeInMB = 5;
      this.showError = false
      this.fileSizeExceeded = false;
      this.selectedFile = file;
      this.selectedFileName = file.name;
      this.editForm.get('placeholder')?.setValue('');
      this.editForm.get('placeholder')?.disable();

      if (fileSizeInMB > maxSizeInMB) {
        this.fileSizeExceeded = true;
        return;
      }
    }
  }

  protected isBothDocumentFieldsInvalid() {
    const placeholder = this.editForm.get('placeholder')?.value;
    return (!this.selectedFileName && !placeholder) || (!this.selectedFileName && placeholder.length == 0)
  }

  protected clearDocument() {
    this.selectedFile = null;
    this.selectedFileName = null;
    this.editForm.get('placeholder')?.enable();
  }

  private initForm() {
    const placeholderFromData = this.data.placeholder;
    const placeholderControl = this.editForm.get('placeholder');

    if (this.isDocument()) {
      if(placeholderFromData && !placeholderFromData.endsWith('.pdf')) {
        placeholderControl?.setValue(placeholderFromData);
        this.hasDocumentURL = true;
      } else {
        this.selectedFileName = placeholderFromData;
        placeholderControl?.disable();
      }
    } else {
      placeholderControl?.setValue(placeholderFromData);
    }
  }

  private getPlaceholder() {
    const placeholder = this.editForm.value.placeholder;

    if(!this.isDocument()) {
      return placeholder;
    } else {
      if(placeholder && placeholder.length > 0) {
        return placeholder;
      } else {
        return this.selectedFileName;
      }
    }
  }
}
